import {
  AccountInfo,
  AuthError,
  AuthenticationResult,
  InteractionRequiredAuthError,
  IPublicClientApplication
} from '@azure/msal-browser';
import { environment } from '../../environments/environment';

const authErrorKey = 'observability.auth.error';
const networkErrorCodes = new Set(['network_error', 'no_network', 'post_request_failed']);
let interactiveSignInStarted = false;
let loginAttempt: Promise<void> | null = null;

export class AuthRequiredError extends Error {
  constructor() {
    super('Signing you in again.');
    this.name = 'AuthRequiredError';
  }
}

export async function acquireApiToken(
  application: IPublicClientApplication,
  forceRefresh = false
): Promise<AuthenticationResult> {
  const account = activeAccount(application);
  if (!account) {
    await startLogin(application);
    throw new AuthRequiredError();
  }

  application.setActiveAccount(account);
  try {
    const result = await application.acquireTokenSilent({
      account,
      forceRefresh,
      scopes: [environment.observabilityService.scope]
    });
    sessionStorage.removeItem(authErrorKey);
    resetInteractiveSignIn();
    return result;
  } catch (error) {
    if (isInteractionInProgress(error)) {
      throw new AuthRequiredError();
    }

    if (requiresInteractiveSignIn(error)) {
      signInForToken(application, account, claimsFrom(error));
      throw new AuthRequiredError();
    }

    throw error;
  }
}

export async function beginSignIn(application: IPublicClientApplication): Promise<void> {
  sessionStorage.removeItem(authErrorKey);
  resetInteractiveSignIn();
  await startLogin(application);
}

export async function ensureAccessToken(application: IPublicClientApplication): Promise<boolean> {
  if (!activeAccount(application) && (getAuthRedirectError() || isAuthRedirectResponse())) {
    return false;
  }

  try {
    await acquireApiToken(application);
    return true;
  } catch (error) {
    if (error instanceof AuthRequiredError) {
      return false;
    }

    throw error;
  }
}

export function getAuthRedirectError(): string | null {
  return sessionStorage.getItem(authErrorKey);
}

export function isAuthRedirectResponse(): boolean {
  const params = `${window.location.hash}${window.location.search}`;
  return params.includes('code=') || params.includes('error=');
}

export function requiresInteractiveSignIn(error: unknown): boolean {
  if (isInteractionInProgress(error) || networkErrorCodes.has(errorCode(error))) {
    return false;
  }

  return error instanceof AuthError;
}

export function resetInteractiveSignIn(): void {
  interactiveSignInStarted = false;
  loginAttempt = null;
}

export function setAuthRedirectError(message: string): void {
  sessionStorage.setItem(authErrorKey, message);
}

export function signInForToken(
  application: IPublicClientApplication,
  account: AccountInfo,
  claims?: string
): void {
  if (interactiveSignInStarted) {
    return;
  }

  interactiveSignInStarted = true;
  void application
    .acquireTokenRedirect({
      account,
      claims,
      scopes: [environment.observabilityService.scope]
    })
    .catch((error: unknown) => {
      resetInteractiveSignIn();
      if (!isInteractionInProgress(error)) {
        setAuthRedirectError(error instanceof Error ? error.message : 'Sign-in failed.');
      }
    });
}

function activeAccount(application: IPublicClientApplication): AccountInfo | null {
  return application.getActiveAccount() ?? application.getAllAccounts()[0] ?? null;
}

function claimsFrom(error: unknown): string | undefined {
  if (error instanceof InteractionRequiredAuthError && error.claims) {
    return error.claims;
  }

  return undefined;
}

function errorCode(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'errorCode' in error) {
    return String((error as { errorCode: unknown }).errorCode);
  }

  return '';
}

function isInteractionInProgress(error: unknown): boolean {
  return errorCode(error) === 'interaction_in_progress';
}

async function startLogin(application: IPublicClientApplication): Promise<void> {
  if (loginAttempt) {
    return loginAttempt;
  }

  interactiveSignInStarted = true;
  loginAttempt = application
    .loginRedirect({
      scopes: [environment.observabilityService.scope]
    })
    .catch((error: unknown) => {
      resetInteractiveSignIn();
      if (!isInteractionInProgress(error)) {
        setAuthRedirectError(error instanceof Error ? error.message : 'Sign-in failed.');
      }
    });
  return loginAttempt;
}
